import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { TIPOS_PARTY, type Party } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

interface Props {
  idioma: Idioma;
  onVolver: () => void;
}

type Tipo = (typeof TIPOS_PARTY)[number];

interface Formulario {
  tipo: Tipo;
  nombre: string;
  pais: string;
  nif: string;
  email: string;
  telefono: string;
  direccionFacturacion: string;
}

const VACIO: Formulario = {
  tipo: 'PERSONA_FISICA',
  nombre: '',
  pais: 'ES',
  nif: '',
  email: '',
  telefono: '',
  direccionFacturacion: '',
};

/**
 * Personas y empresas.
 *
 * El alta exige solo tipo, nombre y pais. Los datos de facturacion —NIF, correo
 * y direccion— se piden aqui pero NO son obligatorios: solo hacen falta cuando
 * esa party vaya a ser el cliente de facturacion de un contrato, y el servidor
 * lo comprueba en ese momento. Un propietario no necesita NIF para ser
 * propietario.
 */
export function Partes({ idioma, onVolver }: Props) {
  const ids = {
    tipo: useId(),
    nombre: useId(),
    pais: useId(),
    nif: useId(),
    email: useId(),
    telefono: useId(),
    direccion: useId(),
  };

  const [partes, setPartes] = useState<Party[] | null>(null);
  const [datos, setDatos] = useState<Formulario>(VACIO);
  const [editando, setEditando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(
    () =>
      api
        .partes()
        .then(({ partes: lista }) => setPartes(lista))
        .catch(() => setError(t(idioma, 'error.servidor'))),
    [idioma],
  );

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const guardar = async (evento: FormEvent) => {
    evento.preventDefault();
    setError(null);
    // Los campos vacios no se envian: un texto en blanco no es un dato.
    const opcionales = Object.fromEntries(
      (['nif', 'email', 'telefono', 'direccionFacturacion'] as const)
        .filter((campo) => datos[campo].trim() !== '')
        .map((campo) => [campo, datos[campo].trim()]),
    );
    try {
      if (editando) {
        await api.editarParty(editando, { nombre: datos.nombre, pais: datos.pais, ...opcionales });
      } else {
        await api.crearParty({
          tipo: datos.tipo,
          nombre: datos.nombre,
          pais: datos.pais,
          ...opcionales,
        });
      }
      setDatos(VACIO);
      setEditando(null);
      await recargar();
    } catch (fallo: unknown) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    }
  };

  const editar = (party: Party) => {
    setDatos({
      tipo: party.tipo,
      nombre: party.nombre,
      pais: party.pais,
      nif: party.nif ?? '',
      email: party.email ?? '',
      telefono: party.telefono ?? '',
      direccionFacturacion: party.direccionFacturacion ?? '',
    });
    setEditando(party.id);
  };

  const campo = (
    clave: keyof Formulario,
    id: string,
    etiqueta: string,
    extra: Record<string, unknown> = {},
  ) => (
    <div className="campo">
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        value={datos[clave] as string}
        onChange={(e) => setDatos({ ...datos, [clave]: e.target.value })}
        {...extra}
      />
    </div>
  );

  return (
    <main>
      <div className="cabecera-seccion">
        <h1>{t(idioma, 'party.titulo')}</h1>
        <button type="button" className="secundario" onClick={onVolver}>
          {t(idioma, 'app.volver')}
        </button>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <section className="tarjeta">
        <h2>{editando ? t(idioma, 'party.editar') : t(idioma, 'party.nueva')}</h2>
        <form className="formulario" onSubmit={(e) => void guardar(e)}>
          <div className="campo">
            <label htmlFor={ids.tipo}>{t(idioma, 'party.tipo')}</label>
            <select
              id={ids.tipo}
              value={datos.tipo}
              disabled={editando !== null}
              onChange={(e) => setDatos({ ...datos, tipo: e.target.value as Tipo })}
            >
              {TIPOS_PARTY.map((valor) => (
                <option key={valor} value={valor}>
                  {t(idioma, `party.${valor}`)}
                </option>
              ))}
            </select>
          </div>

          {campo('nombre', ids.nombre, t(idioma, 'propiedades.nombre'), {
            required: true,
            minLength: 2,
          })}
          {campo('pais', ids.pais, t(idioma, 'party.pais'), { required: true, maxLength: 2 })}
          {campo(
            'nif',
            ids.nif,
            `${t(idioma, 'party.nif')} · ${t(idioma, 'party.nifSoloFacturacion')}`,
          )}
          {campo('email', ids.email, t(idioma, 'auth.email'), { type: 'email' })}
          {campo('telefono', ids.telefono, t(idioma, 'party.telefono'))}
          {campo('direccionFacturacion', ids.direccion, t(idioma, 'party.direccionFacturacion'))}

          <div className="acciones">
            <button type="submit">{t(idioma, 'app.guardar')}</button>
            {editando && (
              <button
                type="button"
                className="secundario"
                onClick={() => {
                  setEditando(null);
                  setDatos(VACIO);
                }}
              >
                {t(idioma, 'app.cancelar')}
              </button>
            )}
          </div>
        </form>
      </section>

      {partes === null && <p>{t(idioma, 'app.cargando')}</p>}

      {partes !== null && partes.length === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'party.vacio')}</p>
        </div>
      )}

      {partes !== null && partes.length > 0 && (
        <div className="tarjeta tabla-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">{t(idioma, 'propiedades.nombre')}</th>
                <th scope="col">{t(idioma, 'party.tipo')}</th>
                <th scope="col">{t(idioma, 'party.pais')}</th>
                <th scope="col">{t(idioma, 'party.nif')}</th>
                <th scope="col">{t(idioma, 'party.direccionFacturacion')}</th>
                <th scope="col">{t(idioma, 'app.editar')}</th>
              </tr>
            </thead>
            <tbody>
              {partes.map((party) => (
                <tr key={party.id}>
                  <td>{party.nombre}</td>
                  <td>{t(idioma, `party.${party.tipo}`)}</td>
                  <td>{party.pais}</td>
                  <td>{party.nif ?? '—'}</td>
                  <td>{party.direccionFacturacion ?? '—'}</td>
                  <td>
                    <button type="button" className="enlace" onClick={() => editar(party)}>
                      {t(idioma, 'app.editar')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
